<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { storeToRefs } from 'pinia'
import { NBreadcrumb, NBreadcrumbItem, NEmpty, NInput } from 'naive-ui'
import { useWikiPagesStore } from '../stores/wiki_pages'
import { categoryLabel } from '../composables/categoryLabel'
import { GENERAL_BUCKET, isGeneralBucket, pageBucket } from '../composables/categoryBucket'
import WikiPageCard from './WikiPageCard.vue'

// Landing page for `/c/:category`. Lists every wiki page in the named
// category with its title, path, and last-updated date. Each row is a
// router link to the page viewer. Reads from the Pinia catalog (already
// loaded by HomeView via wikiPages.fetchAll), so the rendering stays
// reactive when ingest broadcasts add/remove rows mid-view.
const props = defineProps<{
  // Lower-cased on-disk form ("entities", "concepts") matching the
  // category column on WikiPage rows, OR one of the General-bucket
  // keys (`general` / `_top`) which `isGeneralBucket` resolves to the
  // null-category filter.
  category: string
}>()

const { pages } = storeToRefs(useWikiPagesStore())

const isGeneral = computed(() => isGeneralBucket(props.category))

const displayName = computed(() => categoryLabel(props.category))

// Compare on the bucket key so `_top`-stored top-level pages collapse
// into `general` alongside null-category rows. Without this the
// /c/general filter would miss every page the ingest pipeline wrote
// with the `_top` sentinel as its category.
const matchingPages = computed(() => {
  const target = isGeneral.value ? GENERAL_BUCKET : props.category
  return pages.value
    .filter((p) => pageBucket(p) === target)
    .toSorted((a, b) => (a.title || a.path).localeCompare(b.title || b.path))
})

// Client-side filter over the category's pages — a large
// category (entities can be 50+) was an unsearchable scroll. Matches
// title + path; all rows are already in the catalog store.
const filterQuery = ref('')
const visiblePages = computed(() => {
  const q = filterQuery.value.trim().toLowerCase()
  if (!q) return matchingPages.value
  return matchingPages.value.filter((p) => `${p.title ?? ''} ${p.path}`.toLowerCase().includes(q))
})
</script>

<template>
  <section class="pt-4" data-testid="category-view">
    <!-- Breadcrumb header — uses NBreadcrumb for visual parity with
         WikiPageMeta's bar (same font size, separator glyph, and color
         tokens). Two crumbs only: Home (link) › Category (current,
         non-link, since this IS the category page). -->
    <NBreadcrumb class="mb-2" data-testid="category-breadcrumb">
      <NBreadcrumbItem>
        <RouterLink to="/" class="!text-white/60 hover:!text-white no-underline">Home</RouterLink>
      </NBreadcrumbItem>
      <NBreadcrumbItem>
        <span class="text-white/80">{{ displayName }}</span>
      </NBreadcrumbItem>
    </NBreadcrumb>

    <h1 class="page-title mb-1">{{ displayName }}</h1>
    <p class="text-sm text-white/50 m-0 mb-6">
      {{ matchingPages.length }}
      {{ matchingPages.length === 1 ? 'page' : 'pages' }} in this category.
    </p>

    <NEmpty v-if="matchingPages.length === 0" :description="`No pages tagged with ${displayName} yet.`" class="my-12" />

    <template v-else>
      <NInput
        v-model:value="filterQuery"
        size="small"
        clearable
        placeholder="Filter this category…"
        data-testid="category-filter"
        class="max-w-xs mb-4"
      />

      <p v-if="visiblePages.length === 0" class="text-sm text-white/50 m-0" data-testid="category-filter-empty">
        No pages in {{ displayName }} match “{{ filterQuery.trim() }}”.
        <button type="button" class="text-brand-primary hover:underline" @click="filterQuery = ''">Clear</button>
      </p>

      <ul
        v-else
        class="list-none p-0 m-0 grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3"
        data-testid="category-list"
      >
        <WikiPageCard
          v-for="page in visiblePages"
          :key="page.id"
          :path="page.path"
          :title="page.title"
          :updated-at="page.updated_at"
          :entity-type="page.entity_type"
        />
      </ul>
    </template>
  </section>
</template>
